import { InputAdornment, TextField } from "@mui/material";
import { ClipboardEvent, KeyboardEvent, useEffect, useState } from "react";
import { useSession } from "app/hooks/useSession";
import SysColumn from "app/class/sys-column";
import { SharedFieldInputProps } from "./types";
import {
    formatCurrencyValue,
    formatCurrencyAmount,
    getCurrencyCode,
    getCurrencyDecimalPlaces,
    getNumberSeparators,
    getCurrencyRule,
} from "../services/currencyPreferences";
import { useCurrencyPreferences } from "../hooks/useCurrencyPreferences";

interface CurrencyFieldInputProps extends SharedFieldInputProps<string | null> {
    column?: SysColumn;
    currencyCodeOverride?: string | null;
}

export function CurrencyFieldInput({
    value,
    onChange,
    column,
    error = false,
    helperText,
    autoFocus = false,
    fullWidth = true,
    size = "small",
    disabled = false,
    currencyCodeOverride = null,
}: CurrencyFieldInputProps) {
    const { data: user } = useSession();
    const currencyPreferences = useCurrencyPreferences();
    const [displayValue, setDisplayValue] = useState("");
    const [digits, setDigits] = useState("");
    const decimalPlaces = getCurrencyDecimalPlaces(column, currencyPreferences.data, currencyCodeOverride);
    const symbol = getCurrencyRule(getCurrencyCode(column, currencyPreferences.data, currencyCodeOverride), currencyPreferences.data)?.symbol ?? "";
    const isCurrencyReady =
        !currencyPreferences.isLoading &&
        decimalPlaces !== null &&
        !!getNumberSeparators(user, currencyPreferences.data);

    useEffect(() => {
        const normalized = value ? String(value).replace(",", ".") : "";
        const isNegative = normalized.startsWith("-");
        const unsigned = isNegative ? normalized.slice(1) : normalized;
        const [integerPart = "", fractionPart = ""] = unsigned.split(".");
        if (decimalPlaces === null) {
            setDigits("");
            return;
        }

        const unsignedDigits = decimalPlaces === 0
            ? integerPart.replace(/\D/g, "")
            : `${integerPart.replace(/\D/g, "")}${fractionPart.padEnd(decimalPlaces, "0").slice(0, decimalPlaces)}`.replace(/^0+(?=\d)/, "");
        const nextDigits = isNegative && unsignedDigits ? `-${unsignedDigits}` : unsignedDigits;

        setDigits(nextDigits);
    }, [decimalPlaces, value]);

    useEffect(() => {
        const canonicalValue = buildCanonicalValue(digits, decimalPlaces);
        setDisplayValue(canonicalValue === null && digits === "-" ? "-" : formatCurrencyAmount(canonicalValue, decimalPlaces, user, currencyPreferences.data));
    }, [currencyPreferences.data, decimalPlaces, digits, user]);

    const emitChange = (nextDigits: string) => {
        const canonicalValue = buildCanonicalValue(nextDigits, decimalPlaces);

        onChange(canonicalValue);
    };

    const appendDigit = (digit: string) => {
        const isNegative = digits.startsWith("-");
        const unsignedDigits = digits.replace("-", "");
        const nextUnsignedDigits = `${unsignedDigits}${digit}`.replace(/^0+(?=\d)/, "");
        const nextDigits = isNegative ? `-${nextUnsignedDigits}` : nextUnsignedDigits;
        setDigits(nextDigits);
        emitChange(nextDigits);
    };

    const replaceSelectedValue = (nextDigits: string) => {
        setDigits(nextDigits);
        emitChange(nextDigits);
    };

    const removeLastDigit = () => {
        const isNegative = digits.startsWith("-");
        const unsignedDigits = digits.replace("-", "");
        const nextUnsignedDigits = unsignedDigits.slice(0, -1);
        const nextDigits =
            isNegative && nextUnsignedDigits
                ? `-${nextUnsignedDigits}`
                : nextUnsignedDigits;
        setDigits(nextDigits);
        emitChange(nextDigits);
    };

    const toggleNegative = () => {
        const nextDigits = digits.startsWith("-")
            ? digits.slice(1)
            : digits
                ? `-${digits}`
                : "-";

        setDigits(nextDigits);
        emitChange(nextDigits);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (disabled || decimalPlaces === null) return;
        const input = event.target instanceof HTMLInputElement ? event.target : null;

        if (/^\d$/.test(event.key)) {
            event.preventDefault();
            if (input && hasFullDisplaySelection(input)) {
                replaceSelectedValue(event.key);
                return;
            }

            appendDigit(event.key);
            return;
        }

        if (event.key === "." || event.key === ",") {
            event.preventDefault();
            return;
        }

        if (event.key === "-") {
            event.preventDefault();
            if (input && hasFullDisplaySelection(input)) {
                replaceSelectedValue("-");
                return;
            }

            toggleNegative();
            return;
        }

        if (event.key === "Backspace" || event.key === "Delete") {
            event.preventDefault();
            if (input && hasFullDisplaySelection(input)) {
                replaceSelectedValue("");
                return;
            }

            removeLastDigit();
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            setDigits("");
            onChange(null);
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const pastedValue = event.clipboardData.getData("text");
        const isNegative = pastedValue.trim().startsWith("-");
        const unsignedDigits = pastedValue.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
        const nextDigits = isNegative && unsignedDigits ? `-${unsignedDigits}` : unsignedDigits;

        replaceSelectedValue(nextDigits);
    };

    return (
        <TextField
            inputMode="decimal"
            value={displayValue}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onChange={() => undefined}
            onBlur={() => {
                setDisplayValue(formatCurrencyValue(value, column, user, currencyPreferences.data, currencyCodeOverride).replace(`${symbol} `, ""));
            }}
            error={error}
            helperText={helperText}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            size={size}
            disabled={disabled || !isCurrencyReady}
            InputProps={{
                startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
            }}
        />
    );
}

const hasFullDisplaySelection = (input: HTMLInputElement) =>
    input.selectionStart !== null &&
    input.selectionEnd !== null &&
    input.selectionStart === 0 &&
    input.selectionEnd === input.value.length;

const buildCanonicalValue = (
    digits: string,
    decimalPlaces: number | null
) => {
    if (!digits || digits === "-") return null;
    if (decimalPlaces === null) return null;

    const isNegative = digits.startsWith("-");
    const unsignedDigits = digits.replace("-", "");

    if (decimalPlaces === 0) {
        return isNegative ? `-${unsignedDigits}` : unsignedDigits;
    }

    if (unsignedDigits.length <= decimalPlaces) {
        const canonical = `0.${unsignedDigits.padStart(decimalPlaces, "0")}`;
        return isNegative ? `-${canonical}` : canonical;
    }

    const integerPart = unsignedDigits.slice(0, -decimalPlaces);
    const fractionPart = unsignedDigits.slice(-decimalPlaces);
    const canonical = `${integerPart}.${fractionPart}`;

    return isNegative ? `-${canonical}` : canonical;
};
